import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  Fade,
  Grid,
  InputAdornment,
  Modal,
  Switch,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import useDropdowns from "hooks/useDropdowns";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import partnerMemberMassUpdateSchema from "./partnerMemberMassUpdateSchema";
import EditableCellTime from "../../../EditableCellTime";
import EditableCellNumber from "../../../EditableCellNumber";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const PartnerMemberMassUpdateModal = ({
  open,
  setOpen,
  onUpdate,
  eventDate,
  isForPayrollEditor,
}) => {
  const classes = useStyle();

  const [timeInChecked, setTimeInChecked] = useState(false);
  const [timeOutChecked, setTimeOutChecked] = useState(false);
  const [payRateChecked, setPayRateChecked] = useState(false);
  const [billRateChecked, setBillRateChecked] = useState(false);
  const resolver = useYupValidationResolver(partnerMemberMassUpdateSchema);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { dirtyFields, errors },
  } = useForm({
    resolver,
    defaultValues: {},
  });

  const handleSubmitFunc = (values) => {
    const params = {};

    if (timeInChecked) params.timeIn = values.timeIn;
    if (timeOutChecked) params.timeOut = values.timeOut;
    if (payRateChecked) params.payRate = values.payRate;
    if (billRateChecked) params.billRate = values.billRate;

    onUpdate(params);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }} key="text_addNote">
            Update Multiple
          </MDTypography>
          <MDBox p={3}>
            <form onSubmit={handleSubmit(handleSubmitFunc)}>
              <Grid container xs={12} rowSpacing={3} columnSpacing={1}>
                {!isForPayrollEditor && (
                  <>
                    <Grid item xs={1}>
                      <Checkbox
                        checked={timeInChecked}
                        onChange={(e) => setTimeInChecked(e.target.checked)}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Controller
                        name="timeIn"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <EditableCellTime
                            label="Time In"
                            eventDate={eventDate}
                            time={field.value ?? ""}
                            setTime={(val) => setValue("timeIn", val ? val.toISOString() : val)}
                            disabled={!timeInChecked}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <Checkbox
                        checked={timeOutChecked}
                        onChange={(e) => setTimeOutChecked(e.target.checked)}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Controller
                        name="timeOut"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <EditableCellTime
                            label="Time Out"
                            eventDate={eventDate}
                            time={field.value ?? ""}
                            setTime={(val) => setValue("timeOut", val ? val.toISOString() : val)}
                            disabled={!timeOutChecked}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={1}>
                  <Checkbox
                    checked={payRateChecked}
                    onChange={(e) => setPayRateChecked(e.target.checked)}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Controller
                    name="payRate"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <EditableCellNumber
                        label="Pay Rate"
                        amount={field.value ?? ""}
                        setAmount={(val) => setValue("payRate", val ? parseFloat(val) : val)}
                        adornmentSymbol="$"
                        disabled={!payRateChecked}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Checkbox
                    checked={billRateChecked}
                    onChange={(e) => setBillRateChecked(e.target.checked)}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Controller
                    name="billRate"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <EditableCellNumber
                        label="Bill Rate"
                        amount={field.value ?? ""}
                        setAmount={(val) => setValue("billRate", val ? parseFloat(val) : val)}
                        adornmentSymbol="$"
                        disabled={!billRateChecked}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container mt={2} justifyContent="flex-end">
                <Grid container item width="auto" spacing={3} justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      variant="contained"
                      fullWidth
                      onClick={async (e) => {
                        e?.stopPropagation();
                        setOpen(false);
                      }}
                    >
                      <MDTypography variant="body2" color="white">
                        Cancel
                      </MDTypography>
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button type="submit" variant="contained" fullWidth>
                      <MDTypography variant="body2" color="white">
                        Save
                      </MDTypography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default PartnerMemberMassUpdateModal;
